import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Bot, User, Loader2 } from "lucide-react";
import api from "@/lib/api";

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Ciao! Sono l'assistente AI di Equivalencia. Posso aiutarti con:\n\n📊 **Analisi Vendite:**\n• 'Vendite oggi' - profumi e formati venduti oggi\n• 'Vendite ieri/settimana/mese'\n• 'Confronta vendite' - oggi vs ieri\n\n🔍 **Ricerche:**\n• 'Ordini di [nome]' - cerca ordini\n• 'Info su [codice]' - dettagli profumo\n• 'Profumi più venduti'\n• 'Analisi formati'\n\n⚠️ 'Profumi in esaurimento'\n\nCome posso aiutarti?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post("/assistant/chat", {
        message: userMessage,
        session_id: "main"
      });

      if (response.data.success) {
        setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Mi dispiace, si è verificato un errore. Riprova." }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: "assistant", content: "Errore di connessione. Verifica la tua connessione internet." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
          isOpen 
            ? "bg-gray-800 hover:bg-gray-700" 
            : "bg-[#C9A227] hover:bg-[#D4AF37]"
        }`}
        data-testid="ai-assistant-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div 
          className="fixed bottom-36 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          data-testid="ai-assistant-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#C9A227] to-[#D4AF37] px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Assistente AI</h3>
              <p className="text-white/80 text-xs">Equivalencia Equipe</p>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[350px] overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-[#C9A227]/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-[#C9A227]" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#C9A227] text-white rounded-br-md"
                      : "bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm"
                  }`}
                >
                  {msg.content}
                </div>
                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-8 h-8 rounded-full bg-[#C9A227]/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#C9A227]" />
                </div>
                <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                  <Loader2 className="w-5 h-5 text-[#C9A227] animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi una domanda..."
                disabled={loading}
                className="flex-1 bg-gray-100 border-0 focus-visible:ring-1 focus-visible:ring-[#C9A227]"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="bg-[#C9A227] hover:bg-[#D4AF37] text-white px-3"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
